import * as React from 'react';

import {
  STARDOG_CONTEXT_ALL_IRI,
  STARDOG_CONTEXT_DEFAULT_IRI,
  STARDOG_CONTEXT_LOCAL_IRI,
  STARDOG_CONTEXT_NAMED_IRI,
  STARDOG_CONTEXT_VIRTUAL_IRI,
} from 'bones/constants/iris';
import {
  VoiceboxDatabase,
  VoiceboxNamedGraphs,
} from 'bones/constants/voicebox/settings';

export const SPECIAL_GRAPH_IRIS = new Set([
  STARDOG_CONTEXT_ALL_IRI,
  STARDOG_CONTEXT_DEFAULT_IRI,
  STARDOG_CONTEXT_LOCAL_IRI,
  STARDOG_CONTEXT_NAMED_IRI,
  STARDOG_CONTEXT_VIRTUAL_IRI,
]);

const excludeModelGraphs = (
  namedGraphsByModel: Record<string, string[]>
): Set<string> => {
  const modelNamedGraphs = new Set<string>();
  Object.values(namedGraphsByModel).forEach((graphs) => {
    graphs.forEach((graph) => {
      if (!SPECIAL_GRAPH_IRIS.has(graph)) {
        modelNamedGraphs.add(graph);
      }
    });
  });
  return modelNamedGraphs;
};

export const useLocalGraphsFilter = (
  databaseId: string,
  databasesById: Record<string, VoiceboxDatabase>,
  namedGraphs: string[],
  namedGraphsByDatabaseId: Record<string, VoiceboxNamedGraphs>
) =>
  React.useMemo(() => {
    const filtered = new Set<string>();

    const namedGraphMetadata = namedGraphsByDatabaseId[databaseId] || {};

    const modelGraphs = excludeModelGraphs(
      databasesById[databaseId]?.modelGraphs || {}
    );

    const virtualGraphs = new Set<string>();
    const localGraphs = new Set<string>();
    Object.values(namedGraphMetadata).forEach(({ iri, isVirtual, isAlias }) => {
      if (isVirtual) {
        virtualGraphs.add(iri);
      } else if (!isAlias && !modelGraphs.has(iri)) {
        localGraphs.add(iri);
      }
    });

    const addGraph = (namedGraph: string) => {
      if (modelGraphs.has(namedGraph) || virtualGraphs.has(namedGraph)) {
        return;
      }

      if (
        namedGraph === STARDOG_CONTEXT_ALL_IRI ||
        namedGraph === STARDOG_CONTEXT_LOCAL_IRI
      ) {
        localGraphs.forEach((iri) => {
          filtered.add(iri);
        });
        filtered.add(STARDOG_CONTEXT_DEFAULT_IRI);
        return;
      }

      if (namedGraph === STARDOG_CONTEXT_NAMED_IRI) {
        localGraphs.forEach((iri) => {
          filtered.add(iri);
        });
        return;
      }

      if (namedGraph === STARDOG_CONTEXT_DEFAULT_IRI) {
        filtered.add(STARDOG_CONTEXT_DEFAULT_IRI);
        return;
      }

      const metadata = namedGraphMetadata[namedGraph];
      if (!metadata) {
        return;
      }

      if (metadata.isAlias) {
        metadata.aliasIris.forEach(addGraph);
        return;
      }

      if (!SPECIAL_GRAPH_IRIS.has(namedGraph)) {
        filtered.add(namedGraph);
      }
    };

    namedGraphs.forEach(addGraph);

    return filtered;
  }, [databaseId, databasesById, namedGraphs, namedGraphsByDatabaseId]);
